/* ============================================================
   PH PERFUMERÍA
   PH Search Enterprise v1.0
   Componente universal de búsqueda
   ============================================================ */

(function () {
  'use strict';

  if (!window.PH || typeof window.PH.register !== 'function') {
    console.warn(
      '[PH Search] No se encontró PH Core. Carga ph-core.js antes de ph-search.js.'
    );

    return;
  }

  const PH = window.PH;

  const DEFAULTS = {
    minimumCharacters: 2,
    debounceDelay: 280,
    resultsLimit: 8,
    suggestionsLimit: 6,
    loadingDelay: 120,
  };

  const SYNONYMS = {
    savage: 'sauvage',
    savague: 'sauvage',
    soavage: 'sauvage',
    invictuz: 'invictus',
    inviktus: 'invictus',
    bacara: 'baccarat',
    bakara: 'baccarat',
    baccarat540: 'baccarat rouge 540',
    br540: 'baccarat rouge 540',
    profondo: 'acqua di gio profondo',
    acqua: 'acqua di gio',
    blackopium: 'black opium',
    goodgirl: 'good girl',
    tomford: 'tom ford',
    ysl: 'yves saint laurent',
  };

  function normalizeText(value) {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim()
      .replace(/\s+/g, ' ');
  }

  function normalizeQuery(value) {
    const normalized = normalizeText(value);

    if (!normalized) {
      return '';
    }

    const compact = normalized.replace(/\s+/g, '');

    if (SYNONYMS[compact]) {
      return SYNONYMS[compact];
    }

    if (SYNONYMS[normalized]) {
      return SYNONYMS[normalized];
    }

    return normalized;
  }

  function escapeHTML(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function parseBoolean(value, fallback) {
    if (value === undefined || value === null || value === '') {
      return fallback;
    }

    return value === true || value === 'true';
  }

  function parseNumber(value, fallback) {
    const parsed = Number(value);

    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function getConfig(root) {
    return {
      minimumCharacters: parseNumber(
        root.dataset.minimumCharacters,
        DEFAULTS.minimumCharacters
      ),
      debounceDelay: parseNumber(
        root.dataset.debounceDelay,
        DEFAULTS.debounceDelay
      ),
      resultsLimit: parseNumber(
        root.dataset.resultsLimit,
        DEFAULTS.resultsLimit
      ),
      suggestionsLimit: parseNumber(
        root.dataset.suggestionsLimit,
        DEFAULTS.suggestionsLimit
      ),
      loadingDelay: parseNumber(
        root.dataset.loadingDelay,
        DEFAULTS.loadingDelay
      ),
      predictiveSearch: parseBoolean(
        root.dataset.predictiveSearch,
        true
      ),
    };
  }

  function createDebouncedFunction(callback, delay) {
    let timer = null;

    const debounced = function (...args) {
      window.clearTimeout(timer);

      timer = window.setTimeout(function () {
        callback.apply(null, args);
      }, delay);
    };

    debounced.cancel = function () {
      window.clearTimeout(timer);
    };

    return debounced;
  }

  function emit(root, eventName, detail) {
    if (PH.events && typeof PH.events.emit === 'function') {
      PH.events.emit(eventName, detail);
    }

    root.dispatchEvent(
      new CustomEvent(eventName, {
        bubbles: true,
        detail: detail || {},
      })
    );
  }

  function buildPredictiveURL(query, limit) {
    const baseURL =
      window.routes && window.routes.predictive_search_url
        ? window.routes.predictive_search_url
        : '/search/suggest';

    const params = new URLSearchParams({
      q: query,
      'resources[type]': 'product',
      'resources[limit]': String(limit),
      'resources[options][unavailable_products]': 'hide',
      'resources[options][fields]':
        'title,product_type,tag,variants.title,vendor',
      section_id: 'predictive-search',
    });

    return `${baseURL}?${params.toString()}`;
  }

  function extractPredictiveSection(html) {
    const parser = new DOMParser();
    const documentFragment = parser.parseFromString(html, 'text/html');

    return (
      documentFragment.querySelector('#shopify-section-predictive-search') ||
      documentFragment.querySelector('[data-predictive-search]') ||
      documentFragment.body
    );
  }

  function extractProducts(container, limit) {
    const productNodes = Array.from(
      container.querySelectorAll(
        'li[id*="predictive-search-option-product"], .predictive-search__list-item'
      )
    );

    const products = [];

    productNodes.forEach(function (node) {
      if (products.length >= limit) {
        return;
      }

      const link = node.querySelector('a[href*="/products/"]');

      if (!link) {
        return;
      }

      const image = node.querySelector('img');

      const titleNode =
        node.querySelector('.predictive-search__item-heading') ||
        node.querySelector('[class*="heading"]') ||
        node.querySelector('p');

      const priceNode =
        node.querySelector('.price') ||
        node.querySelector('[class*="price"]');

      const metaNode =
        node.querySelector('.predictive-search__item-query-result') ||
        node.querySelector('[class*="vendor"]');

      products.push({
        title: titleNode
          ? titleNode.textContent.trim()
          : link.textContent.trim(),
        url: link.getAttribute('href'),
        image: image
          ? image.getAttribute('src') ||
            image.getAttribute('data-src') ||
            ''
          : '',
        imageAlt: image
          ? image.getAttribute('alt') || ''
          : '',
        price: priceNode
          ? priceNode.textContent.trim()
          : '',
        meta: metaNode
          ? metaNode.textContent.trim()
          : '',
      });
    });

    return products;
  }

  function renderResults(products) {
    if (!products.length) {
      return '';
    }

    const cards = products
      .map(function (product) {
        const image = product.image
          ? `
            <img
              class="ph-search__result-image"
              src="${escapeHTML(product.image)}"
              alt="${escapeHTML(product.imageAlt || product.title)}"
              loading="lazy"
              width="58"
              height="70"
            >
          `
          : `
            <span
              class="ph-search__result-image"
              aria-hidden="true"
            ></span>
          `;

        const meta = product.meta
          ? `
            <div class="ph-search__result-meta">
              ${escapeHTML(product.meta)}
            </div>
          `
          : '';

        const price = product.price
          ? `
            <div class="ph-search__result-price">
              ${escapeHTML(product.price)}
            </div>
          `
          : '';

        return `
          <a
            class="ph-search__result"
            href="${escapeHTML(product.url)}"
            data-ph-result
            data-product-title="${escapeHTML(product.title)}"
          >
            ${image}

            <div class="ph-search__result-content">
              <p class="ph-search__result-title">
                ${escapeHTML(product.title)}
              </p>

              ${meta}
              ${price}
            </div>
          </a>
        `;
      })
      .join('');

    return `
      <div class="ph-search__results-grid">
        ${cards}
      </div>
    `;
  }

  function renderEmpty(query) {
    return `
      <div class="ph-search__empty">
        <p class="ph-search__empty-title">
          No encontramos coincidencias
        </p>

        <p class="ph-search__empty-text">
          Intenta buscar “${escapeHTML(query)}” con otro nombre o explora alguna categoría.
        </p>
      </div>
    `;
  }

  function renderSuggestions(query, suggestionsLimit) {
    const suggestions = [];

    const normalizedQuery = normalizeQuery(query);

    Object.keys(SYNONYMS).forEach(function (key) {
      const destination = SYNONYMS[key];

      if (
        suggestions.length < suggestionsLimit &&
        (key.includes(normalizedQuery) ||
          destination.includes(normalizedQuery))
      ) {
        if (!suggestions.includes(destination)) {
          suggestions.push(destination);
        }
      }
    });

    if (!suggestions.length) {
      return '';
    }

    return suggestions
      .map(function (suggestion) {
        return `
          <button
            class="ph-search__suggestion"
            type="button"
            data-ph-suggestion
            data-value="${escapeHTML(suggestion)}"
          >
            <span
              class="ph-search__suggestion-icon"
              aria-hidden="true"
            >
              🔍
            </span>

            <span class="ph-search__suggestion-content">
              <span class="ph-search__suggestion-title">
                Buscar ${escapeHTML(suggestion)}
              </span>
            </span>
          </button>
        `;
      })
      .join('');
  }

  function setLoading(state, isLoading) {
    state.root.classList.toggle('is-loading', isLoading);

    if (state.loading) {
      state.loading.hidden = !isLoading;
    }

    if (state.clear) {
      state.clear.hidden =
        isLoading || state.input.value.trim() === '';
    }
  }

  function hideResults(state) {
    state.results.hidden = true;
    state.results.innerHTML = '';

    state.root.classList.remove(
      'is-results',
      'is-empty'
    );
  }

  function hideSuggestions(state) {
    if (!state.suggestions) {
      return;
    }

    state.suggestions.hidden = true;
    state.suggestions.innerHTML = '';
  }

  function showSuggestions(state, query) {
    if (!state.suggestions) {
      return;
    }

    const html = renderSuggestions(
      query,
      state.config.suggestionsLimit
    );

    if (!html) {
      hideSuggestions(state);

      return;
    }

    state.suggestions.innerHTML = html;
    state.suggestions.hidden = false;
  }

  function setActiveChip(state, selectedChip) {
    state.chips.forEach(function (chip) {
      const isActive = chip === selectedChip;

      chip.classList.toggle('is-active', isActive);
      chip.setAttribute(
        'aria-pressed',
        isActive ? 'true' : 'false'
      );
    });

    state.activeFilter = selectedChip
      ? selectedChip.dataset.value || ''
      : '';
  }

  function createSearchQuery(state) {
    const baseQuery = normalizeQuery(state.input.value);

    if (!state.activeFilter) {
      return baseQuery;
    }

    return `${baseQuery} ${state.activeFilter}`.trim();
  }

  async function fetchResults(state, query) {
    if (!state.config.predictiveSearch) {
      return [];
    }

    if (state.controller) {
      state.controller.abort();
    }

    state.controller = new AbortController();

    const response = await fetch(
      buildPredictiveURL(query, state.config.resultsLimit),
      {
        signal: state.controller.signal,
        headers: {
          Accept: 'text/html',
        },
      }
    );

    if (!response.ok) {
      throw new Error(
        `Predictive search failed: ${response.status}`
      );
    }

    const html = await response.text();
    const container = extractPredictiveSection(html);

    return extractProducts(
      container,
      state.config.resultsLimit
    );
  }

  async function performSearch(state) {
    const rawQuery = state.input.value.trim();
    const query = createSearchQuery(state);

    if (
      rawQuery.length < state.config.minimumCharacters &&
      !state.activeFilter
    ) {
      hideResults(state);

      if (rawQuery.length > 0) {
        showSuggestions(state, rawQuery);
      } else {
        hideSuggestions(state);
      }

      return;
    }

    hideSuggestions(state);

    state.requestId += 1;

    const requestId = state.requestId;

    window.clearTimeout(state.loadingTimer);

    state.loadingTimer = window.setTimeout(function () {
      setLoading(state, true);
    }, state.config.loadingDelay);

    emit(state.root, 'ph:search:typing', {
      query: rawQuery,
      normalizedQuery: query,
      filter: state.activeFilter,
    });

    try {
      const products = await fetchResults(state, query);

      if (requestId !== state.requestId) {
        return;
      }

      window.clearTimeout(state.loadingTimer);
      setLoading(state, false);

      state.results.hidden = false;

      if (!products.length) {
        state.results.innerHTML = renderEmpty(rawQuery || state.activeFilter);

        state.root.classList.add('is-empty');
        state.root.classList.remove('is-results');

        emit(state.root, 'ph:search:empty', {
          query: rawQuery,
          normalizedQuery: query,
          filter: state.activeFilter,
        });

        return;
      }

      state.results.innerHTML = renderResults(products);

      state.root.classList.add('is-results');
      state.root.classList.remove('is-empty');

      emit(state.root, 'ph:search:results', {
        query: rawQuery,
        normalizedQuery: query,
        filter: state.activeFilter,
        count: products.length,
        products: products,
      });
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      }

      console.error('[PH Search]', error);

      window.clearTimeout(state.loadingTimer);
      setLoading(state, false);

      state.results.hidden = false;
      state.results.innerHTML = renderEmpty(rawQuery);

      state.root.classList.add('is-empty');
      state.root.classList.remove('is-results');

      emit(state.root, 'ph:search:error', {
        query: rawQuery,
        error: error.message,
      });
    }
  }

  function handleInput(state) {
    const hasValue = state.input.value.trim() !== '';

    if (state.clear) {
      state.clear.hidden = !hasValue;
    }

    state.root.classList.toggle('has-value', hasValue);

    state.debouncedSearch();
  }

  function handleClear(state) {
    state.requestId += 1;

    if (state.controller) {
      state.controller.abort();
    }

    state.input.value = '';

    state.root.classList.remove(
      'has-value',
      'is-loading',
      'is-results',
      'is-empty'
    );

    if (state.clear) {
      state.clear.hidden = true;
    }

    if (state.loading) {
      state.loading.hidden = true;
    }

    hideResults(state);
    hideSuggestions(state);

    state.input.focus();

    emit(state.root, 'ph:search:clear', {
      filter: state.activeFilter,
    });
  }

  function handleChipClick(state, chip) {
    const isCurrentlyActive =
      chip.getAttribute('aria-pressed') === 'true';

    if (isCurrentlyActive && chip.dataset.value !== '') {
      const allChip = state.chips.find(function (item) {
        return item.dataset.value === '';
      });

      setActiveChip(state, allChip || null);
    } else {
      setActiveChip(state, chip);
    }

    state.debouncedSearch.cancel();
    performSearch(state);

    emit(state.root, 'ph:search:filter', {
      filter: state.activeFilter,
      query: state.input.value.trim(),
    });
  }

  function handleSuggestionClick(state, suggestion) {
    const value = suggestion.dataset.value || '';

    state.input.value = value;

    state.root.classList.add('has-value');

    if (state.clear) {
      state.clear.hidden = false;
    }

    hideSuggestions(state);

    performSearch(state);

    emit(state.root, 'ph:search:suggestion', {
      value: value,
    });
  }

  function handleResultClick(state, result) {
    emit(state.root, 'ph:search:select', {
      query: state.input.value.trim(),
      normalizedQuery: createSearchQuery(state),
      filter: state.activeFilter,
      title: result.dataset.productTitle || '',
      url: result.getAttribute('href') || '',
    });
  }

  function handleRootClick(state, event) {
    const chip = event.target.closest('[data-ph-chip]');

    if (chip && state.root.contains(chip)) {
      handleChipClick(state, chip);

      return;
    }

    const suggestion = event.target.closest(
      '[data-ph-suggestion]'
    );

    if (suggestion && state.root.contains(suggestion)) {
      handleSuggestionClick(state, suggestion);

      return;
    }

    const result = event.target.closest('[data-ph-result]');

    if (result && state.root.contains(result)) {
      handleResultClick(state, result);
    }
  }

  function handleDocumentClick(state, event) {
    if (state.root.contains(event.target)) {
      return;
    }

    hideSuggestions(state);
  }

  function mount(root) {
    const input = root.querySelector('[data-ph-input]');
    const results = root.querySelector('[data-ph-results]');

    if (!input || !results) {
      console.warn(
        '[PH Search] Estructura incompleta.',
        root
      );

      return null;
    }

    const state = {
      root: root,
      input: input,
      clear: root.querySelector('[data-ph-clear]'),
      loading: root.querySelector('[data-ph-loading]'),
      suggestions: root.querySelector('[data-ph-suggestions]'),
      results: results,
      chips: Array.from(root.querySelectorAll('[data-ph-chip]')),
      config: getConfig(root),
      activeFilter: '',
      requestId: 0,
      controller: null,
      loadingTimer: null,
      debouncedSearch: null,
      handlers: {},
    };

    state.debouncedSearch = createDebouncedFunction(
      function () {
        performSearch(state);
      },
      state.config.debounceDelay
    );

    state.handlers.input = function () {
      handleInput(state);
    };

    state.handlers.focus = function () {
      root.classList.add('is-focused');

      emit(root, 'ph:search:focus', {
        query: input.value.trim(),
      });
    };

    state.handlers.blur = function () {
      root.classList.remove('is-focused');
    };

    state.handlers.clear = function () {
      handleClear(state);
    };

    state.handlers.rootClick = function (event) {
      handleRootClick(state, event);
    };

    state.handlers.documentClick = function (event) {
      handleDocumentClick(state, event);
    };

    input.addEventListener('input', state.handlers.input);
    input.addEventListener('focus', state.handlers.focus);
    input.addEventListener('blur', state.handlers.blur);

    if (state.clear) {
      state.clear.addEventListener(
        'click',
        state.handlers.clear
      );
    }

    root.addEventListener(
      'click',
      state.handlers.rootClick
    );

    document.addEventListener(
      'click',
      state.handlers.documentClick
    );

    const defaultChip = state.chips.find(function (chip) {
      return chip.dataset.value === '';
    });

    if (defaultChip) {
      setActiveChip(state, defaultChip);
    }

    if (input.value.trim()) {
      root.classList.add('has-value');

      if (state.clear) {
        state.clear.hidden = false;
      }
    }

    root.__phSearchState = state;

    return state;
  }

  function destroy(root) {
    const state = root.__phSearchState;

    if (!state) {
      return;
    }

    state.debouncedSearch.cancel();

    window.clearTimeout(state.loadingTimer);

    if (state.controller) {
      state.controller.abort();
    }

    state.input.removeEventListener(
      'input',
      state.handlers.input
    );

    state.input.removeEventListener(
      'focus',
      state.handlers.focus
    );

    state.input.removeEventListener(
      'blur',
      state.handlers.blur
    );

    if (state.clear) {
      state.clear.removeEventListener(
        'click',
        state.handlers.clear
      );
    }

    state.root.removeEventListener(
      'click',
      state.handlers.rootClick
    );

    document.removeEventListener(
      'click',
      state.handlers.documentClick
    );

    delete root.__phSearchState;
  }

  class PHSearch {
    constructor(root) {
      this.root = root;
      mount(root);
    }

    destroy() {
      destroy(this.root);
      this.root = null;
    }
  }

  PH.register('search', PHSearch);

  if (typeof PH.registerAuto === 'function') {
    PH.registerAuto('[data-ph-search]', 'search');
  } else {
    console.warn(
      '[PH Search] PH.registerAuto no está disponible; el montaje automático fue omitido.'
    );
  }

  PH.Search = PH.Search || {};

  PH.Search.search = function (root, query) {
    const state = root && root.__phSearchState;

    if (!state) {
      return;
    }

    state.input.value = String(query || '');

    handleInput(state);
  };

  PH.Search.clear = function (root) {
    const state = root && root.__phSearchState;

    if (!state) {
      return;
    }

    handleClear(state);
  };
})();
